import React from 'react';
import { Card } from './PublicShell';
import MemberSelect from './MemberSelect';
import MetricSummary from './MetricSummary';
import DataTable from './DataTable';
import GeneralTable from './GeneralTable';
import EngineImpact from './EngineImpact';

export default function ReviewBody({
  data,
  mode,
  setMode,
  selected,
  setSelected,
  engineShift,
  setEngineShift,
  refresh,
  loading,
}) {
  const detail = data.individual;
  const lastRosterUpload = data?.general?.lastRosterUpload || 'Unknown';
  const action = (
    <button className="pub-btn" onClick={refresh} disabled={loading}>
      {loading ? 'Refreshing...' : 'Refresh'}
    </button>
  );

  return (
    <>
      <Card title="Deployment Impact" action={action}>
        <div className="pub-mode-tabs" role="tablist" aria-label="Deployment impact type">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'individual'}
            className={`pub-mode-tab${mode === 'individual' ? ' is-active' : ''}`}
            onClick={() => setMode('individual')}
          >
            Individual
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'engine'}
            className={`pub-mode-tab${mode === 'engine' ? ' is-active' : ''}`}
            onClick={() => setMode('engine')}
          >
            Engine Company
          </button>
        </div>

        <div className="pub-note">
          <strong>Planning aid:</strong> This review describes staffing impact from the latest available roster and picklist data. It does not determine individual deployment eligibility, create a deployment restriction, or replace Assistant Chief discretion.
        </div>

        {mode === 'individual' ? (
          <>
            <div className="pub-note">
              <strong>Individual review:</strong> Start typing a member's name to see the staffing impact of deploying that individual over the next seven days. Day 1 considers Full-Day and Partial Picklists separately; Days 2–7 use Full-Day candidates only. The detail focuses on periods the employee is actually scheduled to work and whether backfill candidates are available for those affected periods. The search remains limited to the existing deployment-eligible list and is not a district personnel directory.
            </div>

            <div className="pub-note">
              <strong>Last Roster Upload:</strong> {lastRosterUpload}
            </div>

            <MemberSelect
              items={data.employees}
              value={selected}
              onChange={setSelected}
            />

            {detail && (
              <div style={{ marginTop: 18 }}>
                <MetricSummary item={detail} />
                <DataTable rows={detail.details} />
              </div>
            )}
          </>
        ) : (
          <>
            <div className="pub-note">
              <strong>Engine Company review:</strong> Choose the suppression shift that would supply the engine. The model uses a standard four-person company — 1 Captain, 1 Engineer, and 2 Firefighters — and applies that demand only on the selected platoon's normal 48/96 duty days. It evaluates shift-and-rank capacity only and never ranks or evaluates named personnel.
            </div>

            <div className="pub-note">
              <strong>Last Roster Upload:</strong> {lastRosterUpload}
            </div>

            <EngineImpact
              general={data.general}
              shift={engineShift}
              onShiftChange={setEngineShift}
            />
          </>
        )}
      </Card>

      <Card title="Overall 7-Day Outlook">
        <GeneralTable general={data.general} />
      </Card>

      <div className="pub-footer">
        Staffing information is a snapshot based on the most recent roster and picklist documents uploaded and may not reflect staffing changes that occur afterward. Individual review evaluates the selected deployment-eligible member's staffing impact. Engine Company review is deliberately anonymous and shift-and-rank based. Results are informational planning support only and do not establish whether a particular employee may or may not deploy.
      </div>
    </>
  );
}
